/**
 * Campaign Countdown — reusable Product page block.
 *
 * Defines the <campaign-countdown> custom element used by the "Campaign
 * countdown" block (sections/main-product.liquid → snippets/product-info.liquid,
 * case 'campaign_countdown').
 *
 * The element reads its absolute end time (UTC milliseconds, already computed
 * server-side in the shop's timezone by Liquid's `date` filter) from a
 * `data-end` attribute, so the countdown always resolves to the same real
 * moment in time no matter which timezone the visitor's browser is set to.
 *
 * Multiple instances can exist on the same page without conflicts: each one
 * owns its own interval and only queries elements inside itself.
 */
(function () {
  if (customElements.get('campaign-countdown')) {
    return;
  }

  var SECOND = 1000;
  var MINUTE = SECOND * 60;
  var HOUR = MINUTE * 60;
  var DAY = HOUR * 24;

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  class CampaignCountdownElement extends HTMLElement {
    connectedCallback() {
      this.endTime = parseInt(this.dataset.end, 10);
      this.isDesignMode = this.dataset.designMode === 'true';

      this.daysEl = this.querySelector('[data-cc-days]');
      this.hoursEl = this.querySelector('[data-cc-hours]');
      this.minutesEl = this.querySelector('[data-cc-minutes]');
      this.secondsEl = this.querySelector('[data-cc-seconds]');

      if (isNaN(this.endTime) || !this.daysEl || !this.hoursEl || !this.minutesEl || !this.secondsEl) {
        return;
      }

      this.tick();

      // Avoid double intervals if the browser fires connectedCallback more
      // than once for the same node (e.g. when Theme Editor moves the block).
      if (this.interval) {
        clearInterval(this.interval);
      }

      this.interval = setInterval(this.tick.bind(this), SECOND);

      this.boundUnload = this.handleSectionUnload.bind(this);
      document.addEventListener('shopify:section:unload', this.boundUnload);
    }

    disconnectedCallback() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
      if (this.boundUnload) {
        document.removeEventListener('shopify:section:unload', this.boundUnload);
        this.boundUnload = null;
      }
    }

    handleSectionUnload() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }

    tick() {
      var remaining = this.endTime - Date.now();

      if (remaining <= 0) {
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }

        if (this.isDesignMode) {
          // Keep it visible at 00d : 00t : 00m : 00s while merchants are
          // editing the block, so they can still style an already-expired
          // preview date, without ever showing negative values.
          this.renderTime(0);
        } else {
          this.hidden = true;
        }
        return;
      }

      this.renderTime(remaining);
    }

    renderTime(remainingMs) {
      var totalSeconds = Math.floor(remainingMs / SECOND);
      var days = Math.floor(totalSeconds / (DAY / SECOND));
      var hours = Math.floor((totalSeconds % (DAY / SECOND)) / (HOUR / SECOND));
      var minutes = Math.floor((totalSeconds % (HOUR / SECOND)) / (MINUTE / SECOND));
      var seconds = totalSeconds % (MINUTE / SECOND);

      this.daysEl.textContent = pad(days);
      this.hoursEl.textContent = pad(hours);
      this.minutesEl.textContent = pad(minutes);
      this.secondsEl.textContent = pad(seconds);
    }
  }

  customElements.define('campaign-countdown', CampaignCountdownElement);
})();
